import { supabase } from '../lib/supabase'
import {
  DashCorretorAccessLog,
  HistoricoAcesso,
  Broker,
  DashCorretorParameter,
  DashCorretorCampeaoMesEntry,
  DashCorretorDecoleInfluenciadoresEntry,
  DashCorretorReiImediatosEntry,
  DashCorretorMinimetas2026Entry,
  DashCorretorBateuLevouEntry,
  DashCorretorTopAcessoEntry,
  DashCorretorClassificationRef,
  DashCorretorClassificationUpgradeEntry,
  DashCorretorIndicationsSummaryEntry,
} from '../types/dashcorretor'
import {
  CLASSIFICATION_UPGRADE_LIST_MAX_GAP,
  resolveClassificationProgress,
} from '../utils/classificationTrend'

const SUPABASE_PAGE_SIZE = 1000

async function fetchAllPages<T>(
  buildQuery: (from: number, to: number) => any
): Promise<T[]> {
  const all: T[] = []
  let from = 0
  let hasMore = true

  while (hasMore) {
    const to = from + SUPABASE_PAGE_SIZE - 1
    const { data, error } = await buildQuery(from, to)

    if (error) throw error

    const page = (data || []) as T[]
    all.push(...page)

    if (page.length < SUPABASE_PAGE_SIZE) {
      hasMore = false
    } else {
      from += SUPABASE_PAGE_SIZE
    }
  }

  return all
}

export const dashcorretorService = {
  /** Listar todos os corretores da tabela brokers (busca em lotes para trazer todos os registros) */
  async listBrokers(): Promise<Broker[]> {
    try {
      return await fetchAllPages<Broker>((from, to) =>
        supabase
          .from('brokers')
          .select('id, name, id_scgp, created_at, classification, classification_previous, state, city')
          .eq('status', 'A')
          .order('name')
          .range(from, to)
      )
    } catch (error) {
      console.error('Erro ao listar corretores (brokers):', error)
      throw error
    }
  },

  /** Referência de faixas (nome + min_value) para tendência de classificação. */
  async listClassificationReferences(): Promise<DashCorretorClassificationRef[]> {
    const { data, error } = await supabase
      .from('dashcorretor_classifications')
      .select('name, min_value, color')
      .order('min_value', { ascending: true })

    if (error) throw error

    return ((data || []) as Array<{ name: string; min_value: number | string; color: string | null }>).map(
      (row) => {
        const minVal = typeof row.min_value === 'number' ? row.min_value : Number(row.min_value)
        return {
          name: row.name,
          min_value: Number.isFinite(minVal) ? minVal : 0,
          color: row.color ?? null,
        }
      }
    )
  },

  /** Prêmio pago mensal do corretor (dashcorretor_values.monthly_paid_premium). */
  async getMonthlyPaidPremium(brokerId: string): Promise<number> {
    if (!brokerId) return 0
    const { data, error } = await supabase
      .from('dashcorretor_values')
      .select('monthly_paid_premium')
      .eq('id_broker', brokerId)
      .maybeSingle()

    if (error) throw error
    const raw = data?.monthly_paid_premium
    const value = typeof raw === 'number' ? raw : Number(raw)
    return Number.isFinite(value) ? value : 0
  },

  /**
   * Corretores ativos a até CLASSIFICATION_UPGRADE_LIST_MAX_GAP do próximo min_value.
   * Ordenado pelo menor gap primeiro.
   */
  async listClassificationUpgradeCandidates(): Promise<DashCorretorClassificationUpgradeEntry[]> {
    const classifications = await this.listClassificationReferences()
    const bands = classifications.map((c) => ({ name: c.name, min_value: c.min_value }))

    const brokers = await fetchAllPages<{
      id: string
      name: string
      id_scgp: number | null
      classification: string | null
      state: string | null
    }>((from, to) =>
      supabase
        .from('brokers')
        .select('id, name, id_scgp, classification, state')
        .eq('status', 'A')
        .order('name')
        .range(from, to)
    )

    const premiumByBrokerId = new Map<string, number>()
    const brokerIds = brokers.map((b) => b.id)
    const IN_CHUNK = 200
    for (let i = 0; i < brokerIds.length; i += IN_CHUNK) {
      const chunk = brokerIds.slice(i, i + IN_CHUNK)
      const { data: valueRows, error: valuesError } = await supabase
        .from('dashcorretor_values')
        .select('id_broker, monthly_paid_premium')
        .in('id_broker', chunk)

      if (valuesError) throw valuesError

      ;((valueRows || []) as Array<{ id_broker: string; monthly_paid_premium: number | null }>).forEach(
        (row) => {
          const raw = row.monthly_paid_premium
          const value = typeof raw === 'number' ? raw : Number(raw)
          premiumByBrokerId.set(row.id_broker, Number.isFinite(value) ? value : 0)
        }
      )
    }

    const candidates: DashCorretorClassificationUpgradeEntry[] = []
    for (const broker of brokers) {
      const premium = premiumByBrokerId.get(broker.id) ?? 0
      const progress = resolveClassificationProgress(premium, bands)
      if (
        progress.gap_to_next_classification == null ||
        progress.gap_to_next_classification > CLASSIFICATION_UPGRADE_LIST_MAX_GAP ||
        !progress.next_classification_name ||
        progress.next_min_value == null
      ) {
        continue
      }
      candidates.push({
        broker_id: broker.id,
        broker_code: broker.id_scgp,
        broker_name: broker.name ?? '(Sem nome)',
        classification: broker.classification,
        state: broker.state,
        monthly_paid_premium: progress.monthly_paid_premium,
        gap_to_next_classification: progress.gap_to_next_classification,
        next_classification_name: progress.next_classification_name,
        next_min_value: progress.next_min_value,
      })
    }

    candidates.sort((a, b) => {
      const gapDiff = a.gap_to_next_classification - b.gap_to_next_classification
      if (gapDiff !== 0) return gapDiff
      return a.broker_name.localeCompare(b.broker_name, 'pt-BR')
    })

    return candidates
  },

  // Buscar logs de acesso
  // A tabela dashcorretor_access_logs tem FK: broker_id -> brokers.id (via constraint dashcorretor_access_logs_broker_id_fkey)
  async getAccessLogs(filters?: {
    brokerId?: string
    dateFrom?: string
    dateTo?: string
    status?: 'success' | 'failed'
  }): Promise<DashCorretorAccessLog[]> {
    let query = supabase
      .from('dashcorretor_access_logs')
      .select('*')
      .order('access_timestamp', { ascending: false })

    if (filters?.brokerId) {
      query = query.eq('broker_id', filters.brokerId)
    }

    if (filters?.dateFrom) {
      query = query.gte('access_timestamp', filters.dateFrom)
    }

    if (filters?.dateTo) {
      query = query.lte('access_timestamp', filters.dateTo)
    }

    if (filters?.status) {
      query = query.eq('status', filters.status)
    }

    const pages = await fetchAllPages<DashCorretorAccessLog>((from, to) =>
      query.range(from, to)
    )

    console.log('Logs encontrados:', pages.length, 'registros')
    return pages
  },

  // Buscar histórico de acessos SEM agrupar - mostra cada acesso individualmente
  async getHistoricoAcessos(filters?: {
    corretorCodeQuery?: string
    corretorQuery?: string
    cnpjQuery?: string
    status?: 'success' | 'failed'
    dateFrom?: string
    dateTo?: string
    brokerId?: string
  }): Promise<HistoricoAcesso[]> {
    // Buscar logs com filtros aplicados (já inclui dados do broker via FK)
    const logsData = await this.getAccessLogs({
      status: filters?.status,
      dateFrom: filters?.dateFrom,
      dateTo: filters?.dateTo,
      brokerId: filters?.brokerId
    })
    
    console.log('Total de logs retornados:', logsData.length)

    // Buscar broker_ids únicos dos logs
    const brokerIds = Array.from(new Set(logsData.map(l => l.broker_id).filter(Boolean) as string[]))
    
    console.log('Broker IDs únicos:', brokerIds)

    // Buscar corretores da tabela brokers usando a FK
    // FK: dashcorretor_access_logs.broker_id -> brokers.id (constraint: dashcorretor_access_logs_broker_id_fkey)
    // Buscamos brokers.name para obter o nome do corretor
    const brokersMap = new Map<string, { name: string; cnpj?: string; id_scgp: number | null }>()
    if (brokerIds.length > 0) {
      try {
        console.log('Buscando corretores com IDs:', brokerIds)
        
        // Primeiro, tentar buscar todos os campos para ver a estrutura
        const { data: brokers, error } = await supabase
          .from('brokers')
          .select('*')
          .in('id', brokerIds)

        if (error) {
          console.error('Erro ao buscar corretores da tabela brokers:', error)
          console.error('Detalhes do erro:', JSON.stringify(error, null, 2))
        } else {
          console.log('Corretores encontrados na tabela brokers:', brokers)
          console.log('Número de corretores encontrados:', brokers?.length || 0)
          
          if (brokers && brokers.length > 0) {
            // Verificar estrutura do primeiro broker para debug
            console.log('Estrutura do primeiro broker:', Object.keys(brokers[0]))
            console.log('Primeiro broker completo:', brokers[0])
            
            brokers.forEach((b: any) => {
              // Tentar diferentes nomes de campos possíveis
              const brokerName = b.name || b.nome || b.full_name || b.broker_name || b.company_name || b.id || '(Sem nome)'
              const brokerCnpj = b.cnpj || b.cpf || ''
              
              console.log(`Mapeando broker: id=${b.id}, name=${brokerName}, cnpj=${brokerCnpj}`)
              
              const rawScgp = b.id_scgp
              const idScgp =
                rawScgp == null || rawScgp === ''
                  ? null
                  : Number(rawScgp)
              brokersMap.set(b.id, {
                name: brokerName,
                cnpj: brokerCnpj,
                id_scgp: Number.isFinite(idScgp) ? idScgp : null,
              })
            })
            console.log('BrokersMap final:', Array.from(brokersMap.entries()))
          } else {
            console.warn('Nenhum corretor encontrado na tabela brokers para os IDs:', brokerIds)
          }
        }
      } catch (error) {
        console.error('Erro ao buscar corretores (catch):', error)
        console.error('Stack trace:', error instanceof Error ? error.stack : 'N/A')
      }
    } else {
      console.log('Nenhum broker_id encontrado nos logs para buscar')
    }

    // Criar um registro para cada acesso individual (SEM agrupar)
    let historico: HistoricoAcesso[] = logsData.map(log => {
      // Buscar informações do corretor usando a FK broker_id -> brokers.id
      const info = log.broker_id 
        ? brokersMap.get(log.broker_id)
        : null

      // Usar brokers.name encontrado ou mostrar broker_id se não encontrar
      const corretor = info?.name || (log.broker_id ? `ID: ${log.broker_id}` : '')
      const cnpj = info?.cnpj || ''
      
      // Log para debug apenas se não encontrou o nome
      if (log.broker_id && !info) {
        console.warn(`Log ${log.id}: broker_id=${log.broker_id} não encontrado no brokersMap`)
        console.warn(`BrokersMap tem ${brokersMap.size} entradas`)
        console.warn(`Chaves no map:`, Array.from(brokersMap.keys()))
      }

      return {
        id: log.id,
        corretor,
        cnpj,
        id_scgp: info?.id_scgp ?? null,
        primeiroAcesso: log.access_timestamp,
        ultimoAcesso: log.access_timestamp,
        broker_id: log.broker_id,
        route: log.route,
        status: log.status,
        ip_address: log.ip_address,
        user_agent: log.user_agent,
        error_message: log.error_message,
        created_at: log.created_at,
        access_timestamp: log.access_timestamp
      }
    })
    
    console.log('Total de registros no histórico (sem agrupar):', historico.length)

    if (filters?.corretorCodeQuery?.trim()) {
      const code = filters.corretorCodeQuery.trim()
      historico = historico.filter((h) => String(h.id_scgp ?? '').includes(code))
    }

    // Aplicar filtros de texto (corretor e CNPJ) após buscar os dados
    if (filters?.corretorQuery) {
      const query = filters.corretorQuery.toLowerCase()
      historico = historico.filter(h => h.corretor.toLowerCase().includes(query))
    }

    if (filters?.cnpjQuery) {
      const query = filters.cnpjQuery.toLowerCase()
      historico = historico.filter(h => h.cnpj.toLowerCase().includes(query))
    }

    const resultado = historico.sort((a, b) => 
      new Date(b.ultimoAcesso).getTime() - new Date(a.ultimoAcesso).getTime()
    )
    
    console.log('Resultado final:', resultado.length, 'registros no histórico')
    return resultado
  },

  // Contar total de acessos no histórico (todos os status)
  async getTotalAcessos(): Promise<number> {
    const { count, error } = await supabase
      .from('dashcorretor_access_logs')
      .select('*', { count: 'exact', head: true })

    if (error) throw error
    return count || 0
  },

  // Contar acessos de hoje (todos os status)
  async getAcessosHoje(): Promise<number> {
    const hoje = new Date()
    hoje.setHours(0, 0, 0, 0)
    const hojeStr = hoje.toISOString()

    const amanha = new Date(hoje)
    amanha.setDate(amanha.getDate() + 1)
    const amanhaStr = amanha.toISOString()

    const { count, error } = await supabase
      .from('dashcorretor_access_logs')
      .select('*', { count: 'exact', head: true })
      .gte('access_timestamp', hojeStr)
      .lt('access_timestamp', amanhaStr)

    if (error) throw error
    return count || 0
  },

  /**
   * Uma passada nos logs: quantidade de corretores distintos e top N por número de acessos.
   */
  async getBrokerAccessStats(topN: number = 3): Promise<{
    distinctCorretores: number
    topCorretores: DashCorretorTopAcessoEntry[]
  }> {
    const rows = await fetchAllPages<{ broker_id: string | null }>((from, to) =>
      supabase
        .from('dashcorretor_access_logs')
        .select('broker_id')
        .not('broker_id', 'is', null)
        .order('id', { ascending: true })
        .range(from, to)
    )

    const counts = new Map<string, number>()
    for (const r of rows) {
      const id = r.broker_id
      if (!id) continue
      counts.set(id, (counts.get(id) ?? 0) + 1)
    }

    const distinctCorretores = counts.size
    if (distinctCorretores === 0) {
      return { distinctCorretores: 0, topCorretores: [] }
    }

    const sorted = Array.from(counts.entries()).sort((a, b) => b[1] - a[1])
    const topPairs = sorted.slice(0, topN)

    if (topPairs.length === 0) {
      return { distinctCorretores, topCorretores: [] }
    }

    const topIds = topPairs.map(([bid]) => bid)
    const { data: brokers, error } = await supabase
      .from('brokers')
      .select('id, name, id_scgp')
      .in('id', topIds)

    if (error) throw error

    const brokerMap = new Map<string, { name: string; id_scgp: number | null }>()
    for (const b of brokers || []) {
      const row = b as { id: string; name?: string; id_scgp?: number | string | null }
      const rawScgp = row.id_scgp
      const idScgp =
        rawScgp == null || rawScgp === '' ? null : Number(rawScgp)
      brokerMap.set(row.id, {
        name: row.name?.trim() || `ID: ${row.id}`,
        id_scgp: Number.isFinite(idScgp) ? idScgp : null,
      })
    }

    const topCorretores: DashCorretorTopAcessoEntry[] = topPairs.map(([broker_id, acessos]) => {
      const info = brokerMap.get(broker_id)
      return {
        broker_id,
        nome: info?.name ?? `ID: ${broker_id}`,
        id_scgp: info?.id_scgp ?? null,
        acessos,
      }
    })

    return { distinctCorretores, topCorretores }
  },

  /** Corretores distintos que já possuem pelo menos um registro na tabela de logs (broker_id não nulo). */
  async getDistinctCorretoresComAcesso(): Promise<number> {
    const { distinctCorretores } = await this.getBrokerAccessStats(0)
    return distinctCorretores
  },

  // ============================================
  // PARÂMETROS DO DASHBOARD CORRETOR
  // ============================================

  async listParameters(): Promise<DashCorretorParameter[]> {
    return fetchAllPages<DashCorretorParameter>((from, to) =>
      supabase
        .from('dashcorretor_parameters')
        .select('*')
        .order('key', { ascending: true })
        .range(from, to)
    )
  },

  async updateParameter(
    id: string,
    data: { value: string; description?: string }
  ): Promise<DashCorretorParameter | null> {
    const payload: { value: string; description?: string } = { value: data.value }
    if (data.description !== undefined) {
      payload.description = data.description
    }

    const { data: result, error } = await supabase
      .from('dashcorretor_parameters')
      .update(payload)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return result as DashCorretorParameter
  },

  // ============================================
  // CAMPANHAS / CAMPEÃO DO MÊS
  // ============================================

  async listCampeaoMes(includeValues: boolean = true): Promise<DashCorretorCampeaoMesEntry[]> {
    let rows: any[] = []
    try {
      rows = await fetchAllPages<any>((from, to) =>
        supabase
          .from('dashcorretor_campaigns_2026_cm')
          .select(
            `
            ${includeValues ? 'value,' : ''}
            campaign_id,
            broker_id,
            reference_month,
            campaign:dashcorretor_campaigns_2026 (code, description),
            broker:brokers (name, id_scgp, classification, state)
          `
          )
          .order('reference_month', { ascending: false })
          .range(from, to)
      )
    } catch (error) {
      console.error('Erro ao listar Campeão do Mês:', error)
      throw error
    }

    return rows.map((row) => ({
      value: row.value ?? 0,
      campaign_id: row.campaign_id,
      broker_id: row.broker_id,
      reference_month: row.reference_month,
      campaign_code: row.campaign?.code ?? 0,
      campaign_description: row.campaign?.description ?? null,
      broker_name: row.broker?.name ?? '(Sem nome)',
      broker_code: row.broker?.id_scgp ?? null,
      classification: row.broker?.classification ?? null,
      state: row.broker?.state ?? null,
    }))
  },

  async listDecoleInfluenciadoresVendas(includeValues: boolean = true): Promise<DashCorretorDecoleInfluenciadoresEntry[]> {
    let rows: any[] = []
    try {
      rows = await fetchAllPages<any>((from, to) =>
        supabase
          .from('dashcorretor_campaigns_2026_dv')
          .select(
            `
            id,
            campaign_id,
            broker_id,
            ${includeValues ? 'value,' : ''}
            campaign:dashcorretor_campaigns_2026 (code, description),
            broker:brokers (id_scgp, name, classification, state)
          `
          )
          .order('value', { ascending: false, nullsFirst: false })
          .order('id', { ascending: true })
          .range(from, to)
      )
    } catch (error) {
      console.error('Erro ao listar Decole Influenciadores (Vendas):', error)
      throw error
    }

    return rows.map((row) => ({
      id: row.id,
      campaign_id: row.campaign_id,
      broker_id: row.broker_id,
      value: Number(row.value ?? 0),
      broker_code: row.broker?.id_scgp ?? null,
      broker_name: row.broker?.name ?? '(Sem nome)',
      classification: row.broker?.classification ?? null,
      state: row.broker?.state ?? null,
      campaign_code: row.campaign?.code ?? null,
      campaign_description: row.campaign?.description ?? null,
    }))
  },

  async listDecoleInfluenciadoresProducao(includeValues: boolean = true): Promise<DashCorretorDecoleInfluenciadoresEntry[]> {
    let rows: any[] = []
    try {
      rows = await fetchAllPages<any>((from, to) =>
        supabase
          .from('dashcorretor_campaigns_2026_dp')
          .select(
            `
            id,
            campaign_id,
            broker_id,
            ${includeValues ? 'value,' : ''}
            campaign:dashcorretor_campaigns_2026 (code, description),
            broker:brokers (id_scgp, name, classification, state)
          `
          )
          .order('value', { ascending: false, nullsFirst: false })
          .order('id', { ascending: true })
          .range(from, to)
      )
    } catch (error) {
      console.error('Erro ao listar Decole Influenciadores (Produção):', error)
      throw error
    }

    return rows.map((row) => ({
      id: row.id,
      campaign_id: row.campaign_id,
      broker_id: row.broker_id,
      value: Number(row.value ?? 0),
      broker_code: row.broker?.id_scgp ?? null,
      broker_name: row.broker?.name ?? '(Sem nome)',
      classification: row.broker?.classification ?? null,
      state: row.broker?.state ?? null,
      campaign_code: row.campaign?.code ?? null,
      campaign_description: row.campaign?.description ?? null,
    }))
  },

  // ============================================
  // CAMPANHAS / "REI DOS IMEDIATOS"
  // ============================================
  // Trazer todos os corretores com quantidade > 0,
  // agregando o total por corretor a partir da campanha BLI 2026.
  async listReiImediatos(includeValues: boolean = true): Promise<DashCorretorReiImediatosEntry[]> {
    const rows = await fetchAllPages<any>((from, to) =>
      supabase
        .from('dashcorretor_campaigns_2026_bli')
        .select(
          `
          broker_id,
          ${includeValues ? 'value,' : ''}
          broker:brokers(id_scgp, name, classification, state)
        `
        )
        .gt('value', 0)
        .range(from, to)
    )

    const map = new Map<string, DashCorretorReiImediatosEntry>()

    for (const row of rows) {
      const brokerId = row.broker_id as string
      const broker = row.broker as {
        id_scgp: number | null
        name: string | null
        classification: string | null
        state: string | null
      } | null

      const quantidade = includeValues ? Number(row.value ?? 0) : 0

      const existing = map.get(brokerId)
      if (existing) {
        existing.quantidade += quantidade
      } else {
        map.set(brokerId, {
          broker_id: brokerId,
          broker_code: broker?.id_scgp ?? null,
          broker_name: broker?.name ?? '(Sem nome)',
          classification: broker?.classification ?? null,
          state: broker?.state ?? null,
          quantidade,
        })
      }
    }

    const items = Array.from(map.values())
    return includeValues
      ? items.sort((a, b) => b.quantidade - a.quantidade)
      : items.sort((a, b) => (a.broker_name || '').localeCompare(b.broker_name || '', 'pt-BR'))
  },

  // ============================================
  // CAMPANHAS / "MINIMETAS 2026"
  // ============================================
  // Corretores com quantidade > 0 na campanha MM 2026,
  // agregando o total de `value` por corretor.
  async listMinimetas2026(includeValues: boolean = true): Promise<DashCorretorMinimetas2026Entry[]> {
    const rows = await fetchAllPages<any>((from, to) =>
      supabase
        .from('dashcorretor_campaigns_2026_mm')
        .select(
          `
          broker_id,
          ${includeValues ? 'value,' : ''}
          broker:brokers(id_scgp, name, classification, state)
        `
        )
        .gt('value', 0)
        .range(from, to)
    )

    const map = new Map<string, DashCorretorMinimetas2026Entry>()

    for (const row of rows) {
      const brokerId = row.broker_id as string
      const broker = row.broker as {
        id_scgp: number | null
        name: string | null
        classification: string | null
        state: string | null
      } | null

      const quantidade = includeValues ? Number(row.value ?? 0) : 0

      const existing = map.get(brokerId)
      if (existing) {
        existing.quantidade += quantidade
      } else {
        map.set(brokerId, {
          broker_id: brokerId,
          broker_code: broker?.id_scgp ?? null,
          broker_name: broker?.name ?? '(Sem nome)',
          classification: broker?.classification ?? null,
          state: broker?.state ?? null,
          quantidade,
        })
      }
    }

    const items = Array.from(map.values())
    return includeValues
      ? items.sort((a, b) => b.quantidade - a.quantidade)
      : items.sort((a, b) => (a.broker_name || '').localeCompare(b.broker_name || '', 'pt-BR'))
  },

  // ============================================
  // CAMPANHAS / "BATEU LEVOU"
  // ============================================
  async listBateuLevou(
    appraisalMonth: 6 | 10,
    includeValues: boolean = true
  ): Promise<DashCorretorBateuLevouEntry[]> {
    let rows: any[] = []
    try {
      const queryBuilder = (from: number, to: number) => {
        let query = supabase
          .from('dashcorretor_campaigns_2026_bl')
          .select(
            `
            broker_id,
            ${includeValues ? 'paid_premium, pending_premium,' : ''}
            broker:brokers (id_scgp, name, classification, state)
          `
          )
          .eq('appraisal_month', appraisalMonth)

        if (includeValues) {
          query = query.order('paid_premium', { ascending: false, nullsFirst: false })
        } else {
          query = query.order('broker_id', { ascending: true })
        }

        return query.range(from, to)
      }

      rows = await fetchAllPages<any>(queryBuilder)
    } catch (error) {
      console.error('Erro ao listar Bateu Levou:', error)
      throw error
    }

    const items = rows.map((row) => ({
      broker_id: row.broker_id as string,
      broker_code: row.broker?.id_scgp ?? null,
      broker_name: row.broker?.name ?? '(Sem nome)',
      classification: row.broker?.classification ?? null,
      state: row.broker?.state ?? null,
      paid_premium: includeValues ? Number(row.paid_premium ?? 0) : 0,
      pending_premium: includeValues ? Number(row.pending_premium ?? 0) : 0,
    }))

    if (!includeValues) {
      return items.sort((a, b) => (a.broker_name || '').localeCompare(b.broker_name || '', 'pt-BR'))
    }

    return items.sort((a, b) => {
      if (b.paid_premium !== a.paid_premium) return b.paid_premium - a.paid_premium
      return (a.broker_name || '').localeCompare(b.broker_name || '', 'pt-BR')
    })
  },

  // ============================================
  // INDICAÇÕES PASI (Super APP / Farma)
  // ============================================

  async listPasiAppIndications(): Promise<DashCorretorIndicationsSummaryEntry[]> {
    return listPasiIndicationsFromTable('dashcorretor_app_indications', true)
  },

  async listPasiFarmaIndications(): Promise<DashCorretorIndicationsSummaryEntry[]> {
    return listPasiIndicationsFromTable('dashcorretor_farma_indications', true)
  },

  async getPasiIndicationsSummaryByBrokerId(brokerId: string): Promise<{
    app: DashCorretorIndicationsSummaryEntry | null
    farma: DashCorretorIndicationsSummaryEntry | null
  }> {
    const [appRows, farmaRows] = await Promise.all([
      fetchPasiIndicationRows('dashcorretor_app_indications', brokerId),
      fetchPasiIndicationRows('dashcorretor_farma_indications', brokerId),
    ])

    const appItems = aggregatePasiIndicationsByBroker(appRows, false)
    const farmaItems = aggregatePasiIndicationsByBroker(farmaRows, false)

    return {
      app: appItems[0] ?? null,
      farma: farmaItems[0] ?? null,
    }
  },
}

type PasiIndicationTable = 'dashcorretor_app_indications' | 'dashcorretor_farma_indications'

type PasiIndicationRow = {
  broker_id: string
  status: string
  broker: {
    id_scgp: number | null
    name: string | null
    classification: string | null
    state: string | null
  } | null
}

async function fetchPasiIndicationRows(
  table: PasiIndicationTable,
  brokerId?: string
): Promise<PasiIndicationRow[]> {
  return fetchAllPages<PasiIndicationRow>((from, to) => {
    let query = supabase
      .from(table)
      .select(
        `
        broker_id,
        status,
        broker:brokers(id_scgp, name, classification, state)
      `
      )
      .order('broker_id', { ascending: true })
      .order('sequence', { ascending: true })

    if (brokerId) {
      query = query.eq('broker_id', brokerId)
    }

    return query.range(from, to)
  })
}

function aggregatePasiIndicationsByBroker(
  rows: PasiIndicationRow[],
  onlyBrokersWithActive: boolean
): DashCorretorIndicationsSummaryEntry[] {
  const map = new Map<string, DashCorretorIndicationsSummaryEntry>()

  for (const row of rows) {
    const brokerId = row.broker_id
    const broker = row.broker
    const isAtivo = row.status === 'ativo'

    const existing = map.get(brokerId)
    if (existing) {
      if (isAtivo) existing.quantidade_ativas += 1
      else existing.quantidade_inativas += 1
      existing.quantidade_total += 1
    } else {
      map.set(brokerId, {
        broker_id: brokerId,
        broker_code: broker?.id_scgp ?? null,
        broker_name: broker?.name ?? '(Sem nome)',
        classification: broker?.classification ?? null,
        state: broker?.state ?? null,
        quantidade_ativas: isAtivo ? 1 : 0,
        quantidade_inativas: isAtivo ? 0 : 1,
        quantidade_total: 1,
      })
    }
  }

  let items = Array.from(map.values())
  if (onlyBrokersWithActive) {
    items = items.filter((item) => item.quantidade_ativas > 0)
    items.sort((a, b) => b.quantidade_ativas - a.quantidade_ativas)
  }

  return items
}

async function listPasiIndicationsFromTable(
  table: PasiIndicationTable,
  onlyBrokersWithActive: boolean
): Promise<DashCorretorIndicationsSummaryEntry[]> {
  try {
    const rows = await fetchPasiIndicationRows(table)
    return aggregatePasiIndicationsByBroker(rows, onlyBrokersWithActive)
  } catch (error) {
    console.error(`Erro ao listar indicações PASI (${table}):`, error)
    throw error
  }
}
